import React from "react";
import { motion } from "framer-motion";
import { Crown, Sparkles, BadgeCheck, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useBrand } from "../../utils/useBrand";
import { hasActiveSubscription } from "../../utils/subscription";
import { useAuthGate } from "../../utils/useAuthGate";
import { isAstriaDomain } from "../../utils/branding";

// Same palette the sidebar and the rest of Sanctuary use.
const COLORS = {
  amethyst: "#6D4C92",
  darkAmethyst: "#2D1B44",
  roseGold: "#E5B8AD",
  gold: "#FFD479",
};

const PERKS = [
  "ปลดล็อกทุกฟีเจอร์",
  "อ่านดวงได้ไม่จำกัด",
  "สิทธิ์เข้าถึงเพื่อนคู่คิดก่อนใคร",
];

/** Formats the renewal date, or returns null when there isn't a usable one. */
const formatRenewal = (value?: string): string | null => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("th-TH", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const PremiumCard: React.FC = () => {
  const navigate = useNavigate();
  const { requireAuth } = useAuthGate();
  const { user } = useAuth();
  const brand = useBrand();
  const brandName = isAstriaDomain() ? "Astria" : "HealJai";

  // The card always renders; subscribers see their plan instead of an upsell.
  const isSubscribed = hasActiveSubscription(user);
  const renewsOn = isSubscribed
    ? formatRenewal((user as any)?.subscriptionEndDate)
    : null;

  return (
    <motion.section
      className="w-full mt-5 sm:mt-6"
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      style={{ marginTop: "2.5rem" }}
    >
      <div
        className="relative overflow-hidden rounded-[28px] px-6 py-7 sm:px-9 sm:py-9 shadow-[0_16px_40px_rgba(45,27,68,0.28)] mx-auto"
        style={{
          maxWidth: 900,
          background:
            "linear-gradient(135deg, #2D1B44 0%, #4A2A72 38%, #8B4E9C 68%, #D98BA0 100%)",
          border: "1px solid rgba(255,255,255,0.16)",
        }}
      >
        {/* Aurora blooms echoing the hero gradient, purely decorative */}
        <div
          className="pointer-events-none absolute -top-28 -right-20 w-96 h-96 rounded-full opacity-60"
          style={{
            background:
              "radial-gradient(circle, rgba(255,170,220,0.55) 0%, rgba(165,120,255,0.30) 45%, transparent 72%)",
            filter: "blur(46px)",
          }}
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute -bottom-32 left-1/4 w-80 h-80 rounded-full opacity-45"
          style={{
            background:
              "radial-gradient(circle, rgba(120,175,255,0.45) 0%, rgba(129,240,255,0.22) 50%, transparent 74%)",
            filter: "blur(50px)",
          }}
          aria-hidden="true"
        />

        <div className="relative flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-8">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3">
              <span
                className="flex items-center justify-center w-11 h-11 rounded-2xl flex-shrink-0"
                style={{
                  backgroundColor: "rgba(255,255,255,0.16)",
                  border: "1px solid rgba(255,255,255,0.22)",
                }}
              >
                {isSubscribed ? (
                  <BadgeCheck
                    className="w-[19px] h-[19px]"
                    style={{ color: COLORS.gold }}
                  />
                ) : (
                  <Crown
                    className="w-[19px] h-[19px]"
                    style={{ color: COLORS.gold }}
                  />
                )}
              </span>
              <span
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em]"
                style={{
                  backgroundColor: "rgba(255,255,255,0.14)",
                  color: "#FFFFFF",
                }}
              >
                <Sparkles className="w-3 h-3" style={{ color: COLORS.gold }} />
                {isSubscribed ? "ใช้งานอยู่" : "พรีเมียม"}
              </span>
            </div>

            <h2 className="mt-4 text-2xl sm:text-3xl font-bold leading-tight text-white">
              {isSubscribed
                ? `คุณใช้ ${brandName} Premium อยู่`
                : `ปลดล็อก ${brandName} Premium`}
            </h2>

            <p className="mt-2.5 text-[13px] sm:text-[15px] leading-relaxed text-white/75 max-w-[540px]">
              {isSubscribed
                ? renewsOn
                  ? `คุณใช้ได้ทุกฟีเจอร์และอ่านดวงได้ไม่จำกัด แพ็กเกจของคุณจะต่ออายุในวันที่ ${renewsOn}`
                  : `คุณใช้ได้ทุกฟีเจอร์และอ่านดวงได้ไม่จำกัด ขอบคุณที่สนับสนุน ${brandName}`
                : "เจาะลึกยิ่งขึ้นด้วยการอ่านดวงไม่จำกัดและทุกฟีเจอร์พรีเมียม ไม่ว่าคุณจะอยู่ที่ไหน"}
            </p>

            <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-2">
              {PERKS.map((perk) => (
                <li
                  key={perk}
                  className="flex items-center gap-1.5 text-[12px] sm:text-[13px] text-white/85"
                >
                  {isSubscribed ? (
                    <Check
                      className="w-3 h-3 flex-shrink-0"
                      style={{ color: COLORS.gold }}
                      strokeWidth={3}
                      aria-hidden="true"
                    />
                  ) : (
                    <span
                      className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: COLORS.gold }}
                      aria-hidden="true"
                    />
                  )}
                  {perk}
                </li>
              ))}
            </ul>
          </div>

          <div className="flex-shrink-0 w-full lg:w-auto">
            <button
              type="button"
              onClick={requireAuth(() =>
                navigate(isAstriaDomain() ? "/journey" : "/subscription"),
              )}
              className="w-full lg:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-white px-7 py-3.5
                text-[15px] font-bold text-[#2D1B44] transition-all duration-300
                hover:-translate-y-0.5 hover:shadow-[0_10px_24px_rgba(0,0,0,0.22)] active:scale-[0.98]
                focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-2
                focus-visible:ring-offset-[#2D1B44]"
            >
              {isSubscribed ? "ดูแพ็กเกจปัจจุบันของคุณ" : "เข้าสู่ระบบเพื่อปลดล็อก"}
            </button>
          </div>
        </div>
      </div>
    </motion.section>
  );
};

export default PremiumCard;
